import { type CSATRating } from '@/types/schema';
import { Button } from '@heidi/design-system/button';
import { FormControl, FormField, FormItem } from '@heidi/design-system/form';
import { Textarea } from '@heidi/design-system/textarea';
import { AnimatePresence, useReducedMotion } from 'motion/react';
import * as m from 'motion/react-m';
import { type Control } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { RatingSelector } from './rating-selector';

type CsatPopoverFormValues = {
  stars: CSATRating;
  comment?: string;
};

type CsatPopoverFormFieldsProps = {
  control: Control<CsatPopoverFormValues>;
  hasStarSelected: boolean;
  onDismiss: () => void;
};

export const CsatPopoverFormFields = ({
  control,
  hasStarSelected,
  onDismiss,
}: CsatPopoverFormFieldsProps) => {
  const intl = useIntl();
  const shouldReduceMotion = useReducedMotion();

  return (
    <div className="flex flex-col">
      <FormField
        control={control}
        name="stars"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <RatingSelector
                variant="star"
                stepDownOnReselect
                rateType={field.value ?? null}
                onChangeRating={(rating) => field.onChange(rating ?? undefined)}
              />
            </FormControl>
          </FormItem>
        )}
      />

      <AnimatePresence initial={false}>
        {hasStarSelected && (
          <m.div
            key="expanded-fields"
            initial={shouldReduceMotion ? false : { height: 0, opacity: 0 }}
            animate={{
              height: 'auto',
              opacity: 1,
              transitionEnd: { overflow: 'visible' },
            }}
            exit={
              shouldReduceMotion
                ? undefined
                : { height: 0, opacity: 0, overflow: 'hidden' }
            }
            transition={
              shouldReduceMotion ? { duration: 0 } : { duration: 0.2, ease: 'easeInOut' }
            }
            className="overflow-hidden"
          >
            <div className="flex flex-col gap-3 pt-3">
              <FormField
                control={control}
                name="comment"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Textarea
                        {...field}
                        placeholder={intl.formatMessage({
                          id: '+jhZ4AWe5d',
                          defaultMessage: 'What could we improve?',
                        })}
                        className="min-h-[80px] resize-none"
                        rows={3}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <div className="flex items-center justify-end gap-2">
                <Button type="button" variant="ghost" size="sm" onClick={onDismiss}>
                  <FormattedMessage id="TDaF6JVgG6" defaultMessage="Dismiss" />
                </Button>
                <Button type="submit" size="sm">
                  <FormattedMessage id="wSZR47Y5kj" defaultMessage="Submit" />
                </Button>
              </div>
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </div>
  );
};
